import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Heart } from "lucide-react";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | expired
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState(data.payment_status); return; }
      } catch {
        // keep trying
      }
      tries += 1;
      if (tries >= MAX_POLLS) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const amount = info?.amount ? `$${(info.amount / 100).toFixed(2)}` : "";

  return (
    <div className="rs-grid-bg rs-scanlines min-h-screen text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-[#0F1219] border p-10 text-center relative z-[2] rs-fade-up"
        style={{ borderColor: state === "paid" ? "#00FF66" : state === "checking" ? "#00F0FF" : "#FF003C" }}>
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto text-[#00F0FF] animate-spin mb-6" />
            <h1 className="font-mono-rs text-xl uppercase tracking-wide mb-2">Confirming payment…</h1>
            <p className="text-sm text-slate-400">Hang tight while we verify your donation.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#00FF66] mb-6" />
            <h1 data-testid="payment-success-title" className="font-mono-rs text-2xl uppercase tracking-wide mb-2">Thank you! {amount}</h1>
            <p className="text-sm text-slate-400 mb-8 flex items-center justify-center gap-2">
              <Heart className="w-4 h-4 text-[#D902EE]" /> Your support keeps RadioShift running.
            </p>
            <button data-testid="payment-success-home" onClick={() => navigate("/")}
              className="w-full py-3 bg-[#D902EE]/20 border border-[#D902EE] font-mono-rs uppercase tracking-[0.15em] hover:bg-[#D902EE]/30 transition-all">
              Back to app
            </button>
          </>
        )}
        {["failed", "expired"].includes(state) && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-[#FF003C] mb-6" />
            <h1 className="font-mono-rs text-xl uppercase tracking-wide mb-2">Payment not completed</h1>
            <p className="text-sm text-slate-400 mb-8">We couldn&apos;t confirm your donation. No charge was made if you didn&apos;t finish checkout.</p>
            <button data-testid="payment-retry" onClick={() => navigate("/donate")}
              className="w-full py-3 border border-slate-600 font-mono-rs uppercase tracking-[0.15em] hover:border-white transition-all">
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
